import { useState } from 'react';
import { motionFlags } from '../lib/motionFlags';

/** Edit composition here — one bucket row per size tier. */
export const ORB_FIELD_BUCKETS = [
  {
    id: 'big',
    count: 3,
    sizeMin: 260,
    sizeMax: 340,
    blur: 70,
    durMin: 46,
    durMax: 60,
    opacity: 0.3,
    travel: 40,
  },
  {
    id: 'medium',
    count: 5,
    sizeMin: 120,
    sizeMax: 180,
    blur: 34,
    durMin: 30,
    durMax: 40,
    opacity: 0.34,
    travel: 80,
  },
  {
    id: 'small',
    count: 9,
    sizeMin: 34,
    sizeMax: 64,
    blur: 9,
    durMin: 16,
    durMax: 24,
    opacity: 0.42,
    travel: 140,
  },
];

/**
 * The field's variants (Stage 4, Sam 2026-10-20: keep the orb field - "subtle but smooth" - and give it more going on).
 * Each starts from ORB_FIELD_BUCKETS; `motion` names how an orb moves (CSS in index.css): drift (today: there and back),
 * rise (up from below and out at the top), spark (a tiny bright point twinkling). Chosen by ORB_VARIANT; a dev build
 * can try another with ?orbs=<id> (/lab/motion).
 */
const scaled = (k) => ORB_FIELD_BUCKETS.map((b) => ({ ...b, ...k(b) }));
// Tiny bright points twinkling between the orbs (Sparkle).
const sparks = (count = 30, durMin = 2.6, durMax = 5.5, travel = 12) => ({
  id: 'spark',
  count,
  sizeMin: 4,
  sizeMax: 9,
  blur: 0.6,
  durMin,
  durMax,
  opacity: 0.95,
  travel,
  motion: 'spark',
  palette: [
    [236, 214, 255],
    [214, 170, 255],
  ],
});
export const ORB_VARIANTS = {
  today: {
    label: 'Today',
    note: 'The field as it ships: orbs drifting there and back.',
    buckets: () => ORB_FIELD_BUCKETS.map((b) => ({ ...b, motion: 'drift' })),
  },
  sparkle: {
    label: 'Sparkle (last round)',
    note: "Today's field with tiny bright points twinkling between the orbs.",
    buckets: () => [...ORB_FIELD_BUCKETS.map((b) => ({ ...b, motion: 'drift' })), sparks()],
  },
  rise: {
    label: 'Rise (last round)',
    note: 'Orbs float slowly upward like lanterns and come round again from below.',
    buckets: () =>
      scaled((b) => ({ motion: 'rise', durMin: b.durMin * 1.1, durMax: b.durMax * 1.3 })),
  },
  sparkleFast: {
    label: 'Sparkle, quicker and fuller',
    note: 'Half as many orbs again, drifting about twice as fast and further; more sparkles, twinkling quicker.',
    // Sparkles slow (8-16 s a twinkle) and never twice in one place: each moves somewhere new while it is out (Sam, 2026-10-20).
    buckets: () => [
      ...scaled((b) => ({
        motion: 'drift',
        count: Math.round(b.count * 1.6),
        durMin: b.durMin * 0.5,
        durMax: b.durMax * 0.5,
        travel: b.travel * 1.4,
      })),
      sparks(48, 8, 16, 30),
    ],
  },
  // The plain orbs wander instead of going there and back on one line (Sam, 2026-10-21: "a more random pattern"): each moves
  // sideways and up-down on two clocks of different lengths, so its path curves and does not visibly repeat.
  sparkleWander: {
    label: 'Sparkle, wandering',
    note: 'Sparkle, quicker and fuller, with the orbs wandering on curving paths instead of back and forth.',
    buckets: () => [
      ...scaled((b) => ({
        motion: 'wander',
        count: Math.round(b.count * 1.6),
        durMin: b.durMin * 0.5,
        durMax: b.durMax * 0.5,
        travel: b.travel * 1.4,
      })),
      sparks(48, 8, 16, 30),
    ],
  },
  sparkleRise: {
    label: 'Sparkle rising',
    note: 'Nearly twice the orbs, rising up the screen so the space stays full, with the sparkles between.',
    buckets: () => [
      ...scaled((b) => ({
        motion: 'rise',
        count: Math.round(b.count * 1.9),
        durMin: b.durMin * 0.8,
        durMax: b.durMax * 1.0,
      })),
      sparks(40, 2.2, 4.6),
    ],
  },
  sparkleBlend: {
    label: 'Drift and rise',
    note: 'The big orbs drift as today while smaller ones rise through them, faster; sparkles twinkle between.',
    buckets: () => [
      ...scaled((b) =>
        b.id === 'big'
          ? { motion: 'drift', durMin: b.durMin * 0.7, durMax: b.durMax * 0.7 }
          : {
              motion: 'rise',
              count: Math.round(b.count * 1.8),
              durMin: b.durMin * 0.75,
              durMax: b.durMax * 0.9,
            },
      ),
      sparks(40, 2.2, 4.6),
    ],
  },
};
export const ORB_VARIANT = 'sparkleWander'; // Sam's pick 2026-10-20 (B3, sparkleFast), orbs wandering since 2026-10-21

const FALLBACK_ORB_PALETTE = [
  [139, 47, 201],
  [168, 85, 247],
  [124, 58, 237],
  [176, 77, 240],
  [98, 60, 190],
];

function parseRgbTriplet(value) {
  if (!value?.trim()) return null;
  const parts = value
    .trim()
    .split(/[\s,]+/)
    .map((n) => parseInt(n, 10));
  return parts.length === 3 && parts.every((n) => Number.isFinite(n)) ? parts : null;
}

function readOrbPaletteFromCss() {
  if (typeof document === 'undefined') return FALLBACK_ORB_PALETTE;
  const root = getComputedStyle(document.documentElement);
  const palette = [1, 2, 3, 4, 5].map((i) =>
    parseRgbTriplet(root.getPropertyValue(`--accent-orb-${i}-rgb`)),
  );
  return palette.every(Boolean) ? palette : FALLBACK_ORB_PALETTE;
}

function randomInRange(min, max) {
  return min + Math.random() * (max - min);
}

function pickPaletteRgb(palette) {
  return palette[Math.floor(Math.random() * palette.length)];
}

function radialGradientForRgb([r, g, b], opacity) {
  return `radial-gradient(circle, rgba(${r}, ${g}, ${b}, ${opacity}) 0%, rgba(${r}, ${g}, ${b}, 0) 70%)`;
}

/**
 * The same orb with its blur already in it (2026-10-30 speed audit). Each orb used to carry a live `filter: blur()` - up
 * to 70 px on a 340 px circle, 27 orbs and 48 sparkles, all animating behind every page - and the phone redid every blur
 * on every frame, under glass that blurs again: taps and transitions lagged everywhere. The look is unchanged: the
 * gradient's cone (opacity at the centre down to 0 at R) blurred by a Gaussian of sigma = the old blur is worked out once
 * here and written as the gradient's own stops, on an element grown by 3 sigma each side. Returns { size, background }.
 */
const STOPS = 12;
function preBlurredOrb([r, g, b], opacity, size, sigma) {
  const R = size * 0.7 * Math.SQRT1_2; // the old gradient's 70% of farthest-corner
  if (sigma < 1) return { size, background: radialGradientForRgb([r, g, b], opacity) };
  const outer = R + 3 * sigma,
    step = R / 18,
    k = 1 / (2 * sigma * sigma);
  const at = (d) => {
    let sum = 0;
    for (let x = -R; x <= R; x += step)
      for (let y = -R; y <= R; y += step) {
        const q = Math.hypot(x, y);
        if (q >= R) continue;
        const dx = x - d;
        sum += (1 - q / R) * Math.exp(-(dx * dx + y * y) * k);
      }
    return (sum * step * step * k) / Math.PI;
  };
  const stops = [];
  for (let i = 0; i <= STOPS; i += 1) {
    const t = i / STOPS;
    stops.push(
      `rgba(${r}, ${g}, ${b}, ${(opacity * at(t * outer)).toFixed(4)}) ${(t * 100).toFixed(1)}%`,
    );
  }
  return {
    size: Math.round(outer * 2),
    background: `radial-gradient(circle closest-side, ${stops.join(', ')})`,
  };
}

/** Built once per page load — never re-randomized on rerender. */
function generateOrbField(palette, buckets) {
  const orbs = [];
  let index = 0;

  for (const bucket of buckets) {
    for (let i = 0; i < bucket.count; i += 1) {
      const size = Math.round(randomInRange(bucket.sizeMin, bucket.sizeMax));
      const left = randomInRange(-6, 106);
      const top = bucket.top ? randomInRange(bucket.top[0], bucket.top[1]) : randomInRange(-6, 106);
      const durSeconds = randomInRange(bucket.durMin, bucket.durMax);
      const theta = Math.random() * Math.PI * 2;
      const dx = Math.round(Math.cos(theta) * bucket.travel);
      const dy = Math.round(Math.sin(theta) * bucket.travel);
      const pal = bucket.palette ?? palette;
      const rgb = pickPaletteRgb(pal);
      // rise starts below the screen; the negative delay spreads the orbs up the path from the first frame.
      const motion = bucket.motion ?? 'drift';
      const extra = motion === 'rise' ? { top: `${randomInRange(102, 112)}%` } : {};
      if (motion === 'wander') {
        // Each axis its own reach (either way) and its own clock, 1.3-1.9x apart, so the two never fall back into step.
        const sx = Math.random() < 0.5 ? -1 : 1,
          sy = Math.random() < 0.5 ? -1 : 1;
        const durY = durSeconds * randomInRange(1.3, 1.9);
        Object.assign(extra, {
          '--dx': `${Math.round(sx * bucket.travel * randomInRange(0.6, 1))}px`,
          '--dy': `${Math.round(sy * bucket.travel * randomInRange(0.6, 1))}px`,
          '--dur-y': `${durY.toFixed(1)}s`,
          '--delay-y': `-${(Math.random() * durY).toFixed(2)}s`,
        });
      }
      const delay = Math.random() * durSeconds;

      const drawn = preBlurredOrb(rgb, bucket.opacity, size, bucket.blur);
      const grow = (drawn.size - size) / 2; // grown about the same centre
      orbs.push({
        id: `${bucket.id}-${index}`,
        className: `orb orb--${motion}`,
        style: {
          width: `${drawn.size}px`,
          height: `${drawn.size}px`,
          left: `calc(${left}% - ${grow}px)`,
          top: grow ? `calc(${extra.top ?? `${top}%`} - ${grow}px)` : `${top}%`,
          background: drawn.background,
          '--dx': `${dx}px`,
          '--dy': `${dy}px`,
          '--dur': `${durSeconds}s`,
          '--delay': `-${delay.toFixed(2)}s`,
          ...Object.fromEntries(Object.entries(extra).filter(([key]) => key !== 'top')),
        },
      });
      index += 1;
    }
  }

  return orbs;
}

let orbFieldCache = null;

function getOrbField() {
  if (!orbFieldCache) {
    const v = ORB_VARIANTS[motionFlags().orbs] ?? ORB_VARIANTS[ORB_VARIANT];
    orbFieldCache = generateOrbField(readOrbPaletteFromCss(), v.buckets());
  }
  return orbFieldCache;
}

/** A sparkle at the end of a twinkle (invisible then): somewhere new, drifting a new way, so none repeats its spot. */
function respark(e) {
  const el = e.currentTarget,
    theta = Math.random() * Math.PI * 2,
    r = 10 + Math.random() * 18;
  el.style.left = `${randomInRange(-2, 100).toFixed(1)}%`;
  el.style.top = `${randomInRange(-2, 100).toFixed(1)}%`;
  el.style.setProperty('--dx', `${Math.round(Math.cos(theta) * r)}px`);
  el.style.setProperty('--dy', `${Math.round(Math.sin(theta) * r)}px`);
}

export default function AmbientOrbField() {
  const [orbs] = useState(getOrbField);

  return (
    <div className="orb-field">
      {orbs.map((orb) => (
        <span
          key={orb.id}
          className={orb.className}
          style={orb.style}
          onAnimationIteration={orb.className.includes('orb--spark') ? respark : undefined}
        />
      ))}
    </div>
  );
}
